/**
 * Hikari Vault — Main App
 * ========================
 * Screen flow:
 *   boot → setup-choice → setup-new (pin → bio? → recovery) → vault
 *                       → setup-restore (phrase → pin → bio?) → vault
 *   boot → lock → vault
 */
import { useEffect, useRef, useState, useCallback } from "react";
import {
    decryptBlob,
    deriveKEK,
    deriveVaultId,
    encryptBlob,
    generateMasterKey,
    generateRecoveryPhrase,
    importMasterKey,
    isBiometricAvailable,
    normalizeRecoveryPhrase,
    registerBiometric,
    unwrapMasterKey,
    verifyBiometric,
    wrapMasterKey,
    clearAllLocal,
    cleanLegacyKeys,
    fetchVault,
    getBioCredentials,
    hydrateLocalFromRemote,
    LS,
    ls,
    pushVault,
    setBioCredentials,
    sToast,
    BootScreen,
    SetupScreen,
    LockScreen,
    VaultScreen,
    EditScreen,
    DetailScreen,
    TrashScreen,
    SettingsScreen,
} from "@hikari/vault-core";

const nowISO = () => new Date().toISOString();
const uid = () => Math.random().toString(36).slice(2) + Date.now().toString(36);
const deviceLabel = () => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return "iPhone";
    if (/iPad/.test(ua)) return "iPad";
    if (/Android/.test(ua)) return "Android";
    if (/Mac/.test(ua)) return "Mac";
    if (/Windows/.test(ua)) return "Windows";
    return "This device";
};

export default function App() {
    const [screen, setScreen] = useState("boot");
    const [bioSupported, setBioSupported] = useState(false);

    const [entries, setEntries] = useState([]);
    const [trash, setTrash] = useState([]);
    const [activeEntry, setActiveEntry] = useState(null);

    // Non-sensitive auth metadata for the current session
    const [authMeta, setAuthMeta] = useState(null);
    const [vaultId, setVaultId] = useState(null);

    // Master key stays in memory only
    const masterKeyRef = useRef(null);
    const recoveryPhraseRef = useRef(null); // kept in memory for "Show phrase" in settings

    const [toast, setToast] = useState("");
    const showToast = useCallback((msg, dur = 2500) => {
        setToast(msg);
        setTimeout(() => setToast(""), dur);
    }, []);

    const refreshAuthMeta = useCallback(() => {
        const creds = getBioCredentials();
        setAuthMeta({
            hasBio: creds.length > 0,
            hasBioOnThisDevice: false, // determined at unlock time (try verify)
            hasPin: !!ls.get(LS.PIN_WRAP),
            pinHint: ls.get(LS.PIN_HINT),
            bioCount: creds.length,
        });
    }, []);

    // ───────── BOOT ─────────
    useEffect(() => {
        (async () => {
            cleanLegacyKeys();
            setBioSupported(await isBiometricAvailable());
            const vid = ls.get(LS.VAULT_ID);
            if (!vid) {
                setScreen("setup");
                return;
            }
            // Pull latest from remote before unlock
            const remote = await fetchVault(vid);
            if (remote && remote !== undefined) {
                hydrateLocalFromRemote(vid, remote);
            } else if (remote === null) {
                // vault was deleted on server — treat as fresh setup
                clearAllLocal();
                setScreen("setup");
                return;
            }
            setVaultId(vid);
            const hasPin = !!ls.get(LS.PIN_WRAP);
            const hasBio = getBioCredentials().length > 0;
            if (!hasPin && !hasBio) {
                clearAllLocal();
                setScreen("setup");
                return;
            }
            refreshAuthMeta();
            setScreen("lock");
        })();
    }, [refreshAuthMeta]);

    // ───────── Encrypted persistence ─────────
    const persist = useCallback(
        async (nextEntries, nextTrash) => {
            if (!masterKeyRef.current || !vaultId) return;
            const entriesCt = await encryptBlob(nextEntries, masterKeyRef.current);
            const trashCt = await encryptBlob(nextTrash, masterKeyRef.current);
            ls.set(LS.ENTRIES, entriesCt);
            ls.set(LS.TRASH, trashCt);
            await pushVault(vaultId, {
                entries_encrypted: entriesCt,
                trash_encrypted: trashCt,
            });
        },
        [vaultId],
    );

    const decryptVault = useCallback(async () => {
        const mk = masterKeyRef.current;
        if (!mk) return;
        const e = ls.get(LS.ENTRIES);
        const t = ls.get(LS.TRASH);
        setEntries(e ? (await decryptBlob(e, mk)) || [] : []);
        setTrash(t ? (await decryptBlob(t, mk)) || [] : []);
    }, []);

    // ───────── SETUP: create new vault ─────────
    const completeNewVaultSetup = async ({ pin, pinHint, bioRawId, recoveryPhrase }) => {
        const newVaultId = await deriveVaultId(recoveryPhrase);
        const masterRaw = generateMasterKey();
        const masterKey = await importMasterKey(masterRaw);
        masterKeyRef.current = masterKey;
        recoveryPhraseRef.current = recoveryPhrase;

        const payload = {};
        if (pin) {
            const pinKek = await deriveKEK(pin, "pin:" + newVaultId);
            payload.pin_wrapped_key = await wrapMasterKey(masterRaw, pinKek);
            payload.pin_hint = pinHint || null;
        }
        if (bioRawId) {
            const bioKek = await deriveKEK(bioRawId, "bio:" + newVaultId);
            const wrapped = await wrapMasterKey(masterRaw, bioKek);
            payload.bio_credentials = [
                { credential_id: bioRawId, wrapped_key: wrapped, label: deviceLabel() },
            ];
        } else {
            payload.bio_credentials = [];
        }
        const recKek = await deriveKEK(
            normalizeRecoveryPhrase(recoveryPhrase),
            "recovery:" + newVaultId,
        );
        payload.recovery_wrapped_key = await wrapMasterKey(masterRaw, recKek);

        const entriesCt = await encryptBlob([], masterKey);
        const trashCt = await encryptBlob([], masterKey);
        payload.entries_encrypted = entriesCt;
        payload.trash_encrypted = trashCt;

        // Persist local
        ls.set(LS.VAULT_ID, newVaultId);
        if (payload.pin_wrapped_key) {
            ls.set(LS.PIN_WRAP, payload.pin_wrapped_key);
            if (payload.pin_hint) ls.set(LS.PIN_HINT, payload.pin_hint);
        }
        setBioCredentials(payload.bio_credentials);
        ls.set(LS.RECOVERY_WRAP, payload.recovery_wrapped_key);
        ls.set(LS.ENTRIES, entriesCt);
        ls.set(LS.TRASH, trashCt);

        await pushVault(newVaultId, payload);
        masterRaw.fill(0);

        setVaultId(newVaultId);
        setEntries([]);
        setTrash([]);
        refreshAuthMeta();
        setScreen("vault");
        showToast("Vault created — welcome");
    };

    // ───────── SETUP: restore existing vault from recovery phrase ─────────
    const completeRestore = async ({ phrase, newPin, newPinHint, enrollBio, bioRawId }) => {
        const vid = await deriveVaultId(phrase);
        const remote = await fetchVault(vid);
        if (!remote) {
            showToast("No vault found for that recovery phrase");
            return false;
        }
        // Unwrap master key with the phrase
        const recKek = await deriveKEK(
            normalizeRecoveryPhrase(phrase),
            "recovery:" + vid,
        );
        const masterRaw = await unwrapMasterKey(remote.recovery_wrapped_key, recKek);
        if (!masterRaw) {
            showToast("Recovery phrase didn't match that vault");
            return false;
        }
        const masterKey = await importMasterKey(masterRaw);
        masterKeyRef.current = masterKey;
        recoveryPhraseRef.current = phrase;

        // Prepare updates: set a (possibly new) PIN for this device, optionally enroll bio
        const updates = {};
        if (newPin) {
            const pinKek = await deriveKEK(newPin, "pin:" + vid);
            updates.pin_wrapped_key = await wrapMasterKey(masterRaw, pinKek);
            updates.pin_hint = newPinHint || remote.pin_hint || null;
        }
        const existingBio = remote.bio_credentials || [];
        if (enrollBio && bioRawId) {
            const bioKek = await deriveKEK(bioRawId, "bio:" + vid);
            const wrapped = await wrapMasterKey(masterRaw, bioKek);
            updates.bio_credentials = [
                ...existingBio,
                { credential_id: bioRawId, wrapped_key: wrapped, label: deviceLabel() },
            ];
        }

        // Hydrate local
        hydrateLocalFromRemote(vid, { ...remote, ...updates });

        // Push updates if any
        if (Object.keys(updates).length > 0) {
            await pushVault(vid, updates);
        }
        masterRaw.fill(0);

        setVaultId(vid);
        await decryptVault();
        refreshAuthMeta();
        setScreen("vault");
        showToast("Vault restored — synced from cloud");
        return true;
    };

    // ───────── UNLOCK flows ─────────
    const unlockWithBio = async () => {
        const creds = getBioCredentials();
        const vid = ls.get(LS.VAULT_ID);
        if (creds.length === 0 || !vid) {
            showToast("Biometric not set up on this device");
            return false;
        }
        // Try each enrolled credential — WebAuthn .get() will actually only
        // succeed for the one registered on this device's authenticator.
        for (const cred of creds) {
            const ok = await verifyBiometric(cred.credential_id);
            if (!ok) continue;
            const kek = await deriveKEK(cred.credential_id, "bio:" + vid);
            const masterRaw = await unwrapMasterKey(cred.wrapped_key, kek);
            if (!masterRaw) continue;
            masterKeyRef.current = await importMasterKey(masterRaw);
            masterRaw.fill(0);
            await decryptVault();
            setScreen("vault");
            return true;
        }
        showToast("Face ID failed — no matching credential");
        return false;
    };

    const unlockWithPin = async (pin) => {
        const wrapped = ls.get(LS.PIN_WRAP);
        const vid = ls.get(LS.VAULT_ID);
        if (!wrapped || !vid) return false;
        const kek = await deriveKEK(pin, "pin:" + vid);
        const masterRaw = await unwrapMasterKey(wrapped, kek);
        if (!masterRaw) return false;
        masterKeyRef.current = await importMasterKey(masterRaw);
        masterRaw.fill(0);
        // Refresh from remote before decrypting
        const remote = await fetchVault(vid);
        if (remote) hydrateLocalFromRemote(vid, remote);
        await decryptVault();
        setScreen("vault");
        return true;
    };

    const unlockWithRecovery = async (phrase) => {
        const normalized = normalizeRecoveryPhrase(phrase);
        const vid = await deriveVaultId(normalized);
        const currentVid = ls.get(LS.VAULT_ID);
        // If user forgot PIN on this device, the phrase must match the installed vault
        if (currentVid && vid !== currentVid) {
            return false;
        }
        const wrapped = ls.get(LS.RECOVERY_WRAP);
        if (!wrapped) return false;
        const kek = await deriveKEK(normalized, "recovery:" + vid);
        const masterRaw = await unwrapMasterKey(wrapped, kek);
        if (!masterRaw) return false;
        masterKeyRef.current = await importMasterKey(masterRaw);
        recoveryPhraseRef.current = normalized;
        masterRaw.fill(0);
        const remote = await fetchVault(vid);
        if (remote) hydrateLocalFromRemote(vid, remote);
        await decryptVault();
        setScreen("vault");
        return true;
    };

    // ───────── Manage devices / methods (while unlocked) ─────────
    const addBiometricOnThisDevice = async () => {
        if (!masterKeyRef.current || !vaultId) {
            showToast("Unlock the vault first");
            return false;
        }
        const rawId = await registerBiometric();
        if (!rawId) {
            showToast("Biometric enrollment cancelled or unavailable");
            return false;
        }
        // We need the raw master_key bytes. Re-export from CryptoKey isn't
        // possible since we imported with extractable=false, so we derive it
        // again from the recovery phrase if we have it in memory, or ask the
        // user. In practice after unlock we have the phrase only if they just
        // restored; after PIN unlock we don't. So re-unwrap using PIN.
        const masterRaw = await reacquireMasterRaw();
        if (!masterRaw) {
            showToast("Re-enter your PIN or recovery phrase to enable Face ID");
            return false;
        }
        const bioKek = await deriveKEK(rawId, "bio:" + vaultId);
        const wrapped = await wrapMasterKey(masterRaw, bioKek);
        const existing = getBioCredentials();
        const next = [
            ...existing,
            { credential_id: rawId, wrapped_key: wrapped, label: deviceLabel() },
        ];
        setBioCredentials(next);
        await pushVault(vaultId, { bio_credentials: next });
        masterRaw.fill(0);
        refreshAuthMeta();
        showToast("Face ID enabled on this device");
        return true;
    };

    // Helper: retrieve raw master_key bytes via an auxiliary method we
    // still have context for. This is only invoked inside an already-unlocked
    // session; we ask for PIN implicitly by re-using the stored wrapping.
    const reacquireMasterRaw = async () => {
        // If we have the recovery phrase still in memory (after restore) use it
        if (recoveryPhraseRef.current && vaultId) {
            const kek = await deriveKEK(
                recoveryPhraseRef.current,
                "recovery:" + vaultId,
            );
            const wrapped = ls.get(LS.RECOVERY_WRAP);
            if (wrapped) {
                const raw = await unwrapMasterKey(wrapped, kek);
                if (raw) return raw;
            }
        }
        // Otherwise prompt via a tiny inline flow: the user re-enters their PIN.
        const pin = window.prompt(
            "Re-enter your PIN to authorise this change:",
        );
        if (!pin) return null;
        const kek = await deriveKEK(pin, "pin:" + vaultId);
        const wrapped = ls.get(LS.PIN_WRAP);
        if (!wrapped) return null;
        return await unwrapMasterKey(wrapped, kek);
    };

    const removeBioCredential = async (credentialId) => {
        if (!vaultId) return;
        const next = getBioCredentials().filter(
            (c) => c.credential_id !== credentialId,
        );
        setBioCredentials(next);
        await pushVault(vaultId, { bio_credentials: next });
        refreshAuthMeta();
        showToast("Biometric removed");
    };

    const syncNow = async () => {
        if (!vaultId || !masterKeyRef.current) return;
        const remote = await fetchVault(vaultId);
        if (remote) {
            hydrateLocalFromRemote(vaultId, remote);
            await decryptVault();
            refreshAuthMeta();
            showToast("Synced");
        } else {
            showToast("Could not reach server");
        }
    };

    const getRecoveryPhrase = () => recoveryPhraseRef.current;

    // ───────── Entry operations ─────────
    const saveEntry = async (typeId, fields, editId = null) => {
        let next;
        if (editId) {
            next = entries.map((e) =>
                e.id === editId
                    ? { ...e, type: typeId, fields, updatedAt: nowISO() }
                    : e,
            );
        } else {
            next = [
                {
                    id: uid(),
                    type: typeId,
                    fields,
                    createdAt: nowISO(),
                    updatedAt: nowISO(),
                },
                ...entries,
            ];
        }
        setEntries(next);
        await persist(next, trash);
        showToast("Saved");
    };

    const softDelete = async (id) => {
        const entry = entries.find((e) => e.id === id);
        if (!entry) return;
        const nextEntries = entries.filter((e) => e.id !== id);
        const nextTrash = [{ ...entry, deletedAt: nowISO() }, ...trash];
        setEntries(nextEntries);
        setTrash(nextTrash);
        await persist(nextEntries, nextTrash);
        showToast("Moved to Trash");
    };

    const restoreFromTrash = async (id) => {
        const entry = trash.find((e) => e.id === id);
        if (!entry) return;
        // eslint-disable-next-line no-unused-vars
        const { deletedAt, ...clean } = entry;
        const nextEntries = [clean, ...entries];
        const nextTrash = trash.filter((e) => e.id !== id);
        setEntries(nextEntries);
        setTrash(nextTrash);
        await persist(nextEntries, nextTrash);
        showToast("Restored");
    };

    const purgeTrashEntry = async (id) => {
        const nextTrash = trash.filter((e) => e.id !== id);
        setTrash(nextTrash);
        await persist(entries, nextTrash);
        showToast("Purged permanently");
    };

    const emptyTrash = async () => {
        setTrash([]);
        await persist(entries, []);
        showToast("Trash emptied");
    };

    // ───────── Import / Export ─────────
    const exportVault = async () => {
        if (!masterKeyRef.current) return;
        const payload = {
            version: 2,
            exportedAt: nowISO(),
            entries,
            trash,
        };
        const encrypted = await encryptBlob(payload, masterKeyRef.current);
        const blob = new Blob([encrypted], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `hikari-vault-${nowISO().slice(0, 10)}.hvault`;
        a.click();
        URL.revokeObjectURL(url);
        showToast("Encrypted backup downloaded");
    };

    const importVault = async (fileText) => {
        if (!masterKeyRef.current) {
            showToast("Unlock your vault first");
            return false;
        }
        const decoded = await decryptBlob(fileText.trim(), masterKeyRef.current);
        if (!decoded || !decoded.entries) {
            showToast("Could not decrypt this backup with current vault");
            return false;
        }
        const ids = new Set(entries.map((e) => e.id));
        const newOnes = decoded.entries.filter((e) => !ids.has(e.id));
        const merged = [...newOnes, ...entries];
        setEntries(merged);
        await persist(merged, trash);
        showToast(`Imported ${newOnes.length} entries`);
        return true;
    };

    const lock = () => {
        masterKeyRef.current = null;
        recoveryPhraseRef.current = null;
        setEntries([]);
        setTrash([]);
        setActiveEntry(null);
        setScreen("lock");
    };

    const wipeVault = async () => {
        const vid = ls.get(LS.VAULT_ID);
        masterKeyRef.current = null;
        recoveryPhraseRef.current = null;
        clearAllLocal();
        if (vid) {
            try {
                await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/vault/${encodeURIComponent(vid)}`,
                    { method: "DELETE" },
                );
            } catch (e) {
                /* ignore */
            }
        }
        setEntries([]);
        setTrash([]);
        setAuthMeta(null);
        setVaultId(null);
        setScreen("setup");
        showToast("Vault wiped");
    };

    // ───────── Render dispatch ─────────
    return (
        <>
            {screen === "boot" && <BootScreen />}
            {screen === "setup" && (
                <SetupScreen
                    bioSupported={bioSupported}
                    onCompleteNew={completeNewVaultSetup}
                    onCompleteRestore={completeRestore}
                    generateRecoveryPhrase={generateRecoveryPhrase}
                    registerBiometric={registerBiometric}
                    showToast={showToast}
                />
            )}
            {screen === "lock" && (
                <LockScreen
                    authMeta={authMeta}
                    onUnlockBio={unlockWithBio}
                    onUnlockPin={unlockWithPin}
                    onUnlockRecovery={unlockWithRecovery}
                    showToast={showToast}
                />
            )}
            {screen === "vault" && (
                <VaultScreen
                    entries={entries}
                    trashCount={trash.length}
                    onOpenEntry={(e) => {
                        setActiveEntry(e);
                        setScreen("detail");
                    }}
                    onNewEntry={() => {
                        setActiveEntry(null);
                        setScreen("edit");
                    }}
                    onLock={lock}
                    onNavigate={setScreen}
                />
            )}
            {screen === "edit" && (
                <EditScreen
                    entry={activeEntry}
                    onSave={async (typeId, fields) => {
                        await saveEntry(typeId, fields, activeEntry?.id);
                        setActiveEntry(null);
                        setScreen("vault");
                    }}
                    onCancel={() => {
                        setActiveEntry(null);
                        setScreen("vault");
                    }}
                    onDelete={async () => {
                        if (activeEntry) await softDelete(activeEntry.id);
                        setActiveEntry(null);
                        setScreen("vault");
                    }}
                />
            )}
            {screen === "detail" && activeEntry && (
                <DetailScreen
                    entry={activeEntry}
                    onEdit={() => setScreen("edit")}
                    onBack={() => {
                        setActiveEntry(null);
                        setScreen("vault");
                    }}
                    onDelete={async () => {
                        await softDelete(activeEntry.id);
                        setActiveEntry(null);
                        setScreen("vault");
                    }}
                    showToast={showToast}
                />
            )}
            {screen === "trash" && (
                <TrashScreen
                    trash={trash}
                    onRestore={restoreFromTrash}
                    onPurge={purgeTrashEntry}
                    onEmpty={emptyTrash}
                    onBack={() => setScreen("vault")}
                />
            )}
            {screen === "settings" && (
                <SettingsScreen
                    authMeta={authMeta}
                    vaultId={vaultId}
                    bioSupported={bioSupported}
                    bioCredentials={getBioCredentials()}
                    onExport={exportVault}
                    onImport={importVault}
                    onWipe={wipeVault}
                    onBack={() => setScreen("vault")}
                    onAddBio={addBiometricOnThisDevice}
                    onRemoveBio={removeBioCredential}
                    onSyncNow={syncNow}
                    getRecoveryPhrase={getRecoveryPhrase}
                    showToast={showToast}
                />
            )}

            {toast && (
                <div style={sToast} data-testid="toast">
                    {toast}
                </div>
            )}
        </>
    );
}
